import AlarmIcon from "@mui/icons-material/Alarm";
import SyncIcon from "@mui/icons-material/Sync";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { stripedFill } from "../../config/eventTypes";
import type { EventRun } from "../../utils/dayHourGrid";
import { CarryIcon } from "../CarryMarker";
import { sectionTooltip } from "./sectionTooltip";
import { tooltipSlotProps } from "./tooltipSlotProps";

type Props = {
	runs: EventRun[];
	laneCount: number;
	/** page-level so an event spanning midnight lights up on both boards */
	hoveredRunKey: string | null;
	onRunHover: (key: string | null) => void;
};

export const EventRunsLayer = ({
	runs,
	laneCount,
	hoveredRunKey,
	onRunHover,
}: Props) => (
	<Box
		sx={{
			position: "absolute",
			left: 0,
			right: 0,
			top: "1px",
			bottom: "1px",
			// hits pass through to the shift bands; only the event blocks
			// themselves catch the pointer (events win hover)
			pointerEvents: "none",
		}}
	>
		{runs.map((run) => {
			const radiusLeft = run.clippedStart ? "0" : "4px";
			const radiusRight = run.clippedEnd ? "0" : "4px";
			const isBreak = run.event.EventTypeCode === "B";
			const BreakIcon = run.isSpell ? SyncIcon : AlarmIcon;
			return (
				<Tooltip
					key={run.key}
					arrow
					slotProps={tooltipSlotProps}
					title={sectionTooltip(run)}
				>
					<Box
						onMouseEnter={() => onRunHover(run.key)}
						onMouseLeave={() => onRunHover(null)}
						sx={{
							position: "absolute",
							pointerEvents: "auto",
							left: `${run.leftPct}%`,
							width: `${run.widthPct}%`,
							minWidth: "10px",
							top: `${(run.lane / laneCount) * 100}%`,
							height: `calc(${100 / laneCount}% - 1px)`,
							boxSizing: "border-box",
							display: "flex",
							alignItems: "center",
							justifyContent: isBreak ? "center" : undefined,
							px: isBreak ? 0 : 0.75,
							overflow: "hidden",
							backgroundColor: run.colors.bg,
							...(run.isSpell && stripedFill(run.colors.color)),
							border: `1px solid ${alpha(run.colors.color, 0.35)}`,
							...(run.isSpell && { borderStyle: "dashed" }),
							borderLeft: run.clippedStart
								? "none"
								: `4px solid ${run.colors.color}`,
							...(run.clippedEnd && { borderRight: "none" }),
							borderRadius: `${radiusLeft} ${radiusRight} ${radiusRight} ${radiusLeft}`,
							zIndex: hoveredRunKey === run.key ? 2 : 1,
							transition: "filter 0.15s ease, box-shadow 0.15s ease",
							...(hoveredRunKey === run.key && {
								filter: "brightness(1.05)",
								boxShadow: `0 0 0 1.5px ${run.colors.color}, 0 2px 6px #0000002E`,
								...(!isBreak && { minWidth: "max-content" }),
							}),
						}}
					>
						{isBreak ? (
							<BreakIcon sx={{ fontSize: 12, color: run.colors.color }} />
						) : (
							<Typography
								variant="caption"
								noWrap
								sx={{
									color: run.colors.color,
									fontWeight: 700,
									fontSize: 11,
									lineHeight: 1.1,
								}}
							>
								<CarryIcon event={run.event} size={12} />
								{run.label}
							</Typography>
						)}
					</Box>
				</Tooltip>
			);
		})}
	</Box>
);
